import axios from "axios";
import * as Sentry from "@sentry/react";
import * as React from "react";
import { Alert } from "react-bootstrap";
import { RouteComponentProps, withRouter } from "react-router-dom";
import Cookies from "universal-cookie";
import Api from "../lib/api";
import { validateSurveyDefinition } from "../lib/Definition";
import { ParticipantApplication } from "../lib/ParticipantApplication";
import { Survey as SurveyModel } from "../lib/Survey";
import { mapSurveyTypeForUrl } from "../lib/surveyTypeMapper";
import Survey from "./Survey";
import acpLogoImg from "../acp-logo-2019.color.png";

interface Props {
  uuid: string;
  pane?: string;
}

interface DangerouslySetInnerHTML {
  __html: string;
}

interface State {
  loading: boolean;
  error: string | null;
  survey: SurveyModel | null;
  application: ParticipantApplication | null;
  thankYouHtml?: DangerouslySetInnerHTML;
}

const api = new Api();
const defaultState = {
  loading: true,
  error: null,
  survey: null,
  application: null,
};

class SurveyLoader extends React.Component<RouteComponentProps<Props>, State> {
  constructor(props: RouteComponentProps<Props>) {
    super(props);
    this.state = defaultState;
  }

  async componentDidMount() {
    // Tag the appId from the URL as early as possible, so even a crash
    // during the initial load is attributable (this load path is where
    // APPLY-42 fired with no user context).
    Sentry.setUser({ id: this.props.match.params.uuid });

    try {
      // load application data from backend.
      const appl = await api.getApplicationById(this.props.match.params.uuid);

      if (!appl) {
        await this.notFound();
        return;
      }

      // Refine with the real identity now that the application has loaded,
      // before survey.init() does any options fetching.
      Sentry.setUser({
        email: appl.email,
        applicationId: appl.applicationId,
      });

      if (appl.submittedAt) {
        const type = mapSurveyTypeForUrl(appl.surveyDefinitionUrl);
        this.props.history.replace(`/thanks/${type}/${appl.applicationId}`);
        return;
      }

      // Load and validate survey definition
      if (!this.surveysMap.has(appl.surveyDefinitionUrl)) {
        throw new Error(
          `Invalid survey definition: ${appl.surveyDefinitionUrl}`,
        );
      }
      const actualSurveyDefinitionUrl =
        this.surveysMap.get(appl.surveyDefinitionUrl) +
        "?t=" +
        new Date().getTime();

      const surveyDef = validateSurveyDefinition(
        (await axios.get(actualSurveyDefinitionUrl!)).data,
      );

      // Initialize survey model
      const survey = new SurveyModel(
        surveyDef,
        appl.applicationId,
        appl.answers,
      );
      await survey.init();

      const cookies = new Cookies();
      cookies.remove("referred-by");

      this.setState({
        application: appl,
        survey,
        loading: false,
      });
    } catch (e) {
      console.error(e);
      this.setState(() => {
        throw e;
      });
    }
  }

  private readonly base = "/survey-definitions/";

  private readonly surveysMap = new Map<string, string>([
    ["mentor", `${this.base}mentor-corp.json`],
    ["mentor-corp", `${this.base}mentor-corp.json`],
    ["mentor-ctzn", `${this.base}mentor-ctzn.json`],
    ["protege-spouse", `${this.base}protege-spouse.json`],
    ["protege", `${this.base}protege.json`],
  ]);

  private readonly thankyouPageMap = new Map<string, string>([
    ["mentor-corp", `${this.base}mentor.html`],
    ["mentor-ctzn", `${this.base}mentor-ctzn.html`],
    ["protege-spouse", `${this.base}protege.html`],
    ["protege", `${this.base}protege.html`],
  ]);

  render() {
    if (this.state.loading) return this.loadingContent();
    if (this.state.thankYouHtml)
      return <div dangerouslySetInnerHTML={this.state.thankYouHtml} />;
    if (this.state.error) {
      return (
        <Alert bsStyle="warning">
          Error: <pre>{this.state.error}</pre>
        </Alert>
      );
    }
    if (this.state.survey) {
      return (
        <Survey
          application={this.state.application!}
          model={this.state.survey}
          activePaneNum={Number(this.props.match.params.pane || 1)}
          loadThankYou={this.loadThankYou}
        />
      );
    }
    return "error";
  }

  loadThankYou = async (
    appl: ParticipantApplication = this.state.application!,
  ) => {
    const actualPageUrl = this.thankyouPageMap.has(appl?.surveyDefinitionUrl)
      ? this.thankyouPageMap.get(appl.surveyDefinitionUrl)
      : this.thankyouPageMap.get("protege");
    const html = (await axios.get(actualPageUrl!)).data;
    console.info(`Loaded tyHTML from: ${actualPageUrl}`, { html });
    this.setState({
      thankYouHtml: { __html: html },
      loading: false,
    });
  };

  notFound = async () => {
    this.setState({
      thankYouHtml: {
        __html: (await axios.get(`${this.base}application-not-found.html`))
          .data,
      },
      loading: false,
    });
  };

  loadingContent() {
    return (
      <div
        style={{
          position: "absolute",
          top: "0",
          left: "0",
          height: "100%",
          width: "100%",
          background: "white",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <style>
          {`
            @keyframes pulse {
              0%, 100% {
                transform: scale(1);
                opacity: 1;
              }
              50% {
                transform: scale(1.05);
                opacity: 0.9;
              }
            }
            .loading-logo {
              animation: pulse 1.5s ease-in-out infinite;
            }
          `}
        </style>
        <img
          src={acpLogoImg}
          alt="Loading"
          className="loading-logo"
          style={{ width: "200px", height: "auto" }}
        />
        <p style={{ marginTop: "20px", color: "#666", fontSize: "14px" }}>
          Loading...
        </p>
      </div>
    );
  }
}

const SurveyLoaderWithRouter = withRouter(SurveyLoader);
export default SurveyLoaderWithRouter;
